/* lib dialogs
 ========================================================================== */
import Dialogs from './a11y-dialog-component';
import routes from '../../routes';
import retrieveFormData from '../cart/retrieveFormData';
import { on } from '../../events';
import { setHTMLWithScripts } from '../../utils';
export const MODAL_LOAD_EVENT = 'load';
export const MODAL_CLOSE_EVENT = 'close';
const MODALS_CREATED = [];
const OTHER_MODALS_CREATED = [];
let dialogDelegatesInitialized = false;
let ajaxModalContainersInitialized = false;
/**
 * By default async modals will have their content loaded when opened using the [data-modal-url] attribute.
 * Content will be cached by default, and exceptions can be added to this function (like quickshop for example)
 *
 * All async modals can listen to a custom "load" event, for example to init dynamic components.
 *
 */
const onOpenModal = (dialog, trigger) => {
    if (trigger) {
        let ajaxUrl = trigger.dataset.modalUrl;
        if (ajaxUrl) {
            const modalContentContainer = dialog.querySelector('[data-modal-content]');
            const modalClass = trigger.dataset.modalClass;
            // @ts-ignore This is a hack to attach data to the dom element
            if (!dialog.defaultClassName) {
                // @ts-ignore This is a hack to attach data to the dom element
                dialog.defaultClassName = dialog.className;
            }
            // @ts-ignore This is a hack to attach data to the dom element
            dialog.previousHTML = modalContentContainer.innerHTML;
            // Classes on open only (kept during close animation). Default when no data-modal-class.
            // @ts-ignore This is a hack to attach data to the dom element
            dialog.className = modalClass || dialog.defaultClassName;
            const linkedForm = trigger.dataset.form && (trigger.form || document.querySelector(trigger.dataset.form));
            // eslint-disable-next-line prefer-destructuring
            const query = trigger.dataset.query;
            if (!modalContentContainer) {
                // eslint-disable-next-line no-console
                console.warn('Missing [data-modal-content] for async dialog', dialog);
            }
            if (query) {
                ajaxUrl = `${ajaxUrl}?${query}`;
            }
            const options = {
                method: linkedForm ? 'POST' : 'GET',
                body: linkedForm ? retrieveFormData(linkedForm) : null,
            };
            fetch(ajaxUrl, options).then((response) => {
                if (response.status === 401) {
                    setTimeout(() => {
                        window.redirect(`${routes.connect}?redirect=${window.location.href}`);
                    }, 3000);
                }
                response.text().then((content) => {
                    setHTMLWithScripts(modalContentContainer, content);
                    if (trigger.dataset.labelledby) {
                        dialog.setAttribute('aria-labelledby', trigger.dataset.labelledby);
                    }
                    dialog.dispatchEvent(new CustomEvent(MODAL_LOAD_EVENT, { detail: { role: trigger.dataset.role } }));
                });
            });
        }
        else {
            dialog.dispatchEvent(new CustomEvent(MODAL_LOAD_EVENT, { detail: { role: trigger.dataset.role } }));
        }
    }
};
/**
 * Used to reset modal to loading state if needed
 *
 * @param {Dialogs.Dialog} instance
 */
const onCloseModal = (instance) => {
    if (instance.trigger && instance.trigger.dataset.modalUrl) {
        instance.dialog.dispatchEvent(new CustomEvent(MODAL_CLOSE_EVENT, { detail: { role: instance.trigger.dataset.role } }));
        const dialog = instance.dialog;
        const modalContentContainer = dialog.querySelector('[data-modal-content]');
        // @ts-ignore snapshotted in onOpenModal
        const previousHTML = dialog.previousHTML;
        if (modalContentContainer) {
            modalContentContainer.innerHTML = previousHTML;
        }
    }
};
// init dialogs based on data components
export function initDialogs({ documentClass = 'js-document', documentDisabledClass = 'is-disabled', documentFixedClass = 'is-fixed', triggerActiveClass = 'is-active', } = {}) {
    if (!dialogDelegatesInitialized) {
        dialogDelegatesInitialized = true;
        on('[data-component="dialog"]', 'click', (element) => {
            let triggerId = element.id;
            const target = element.dataset.target;
            // Prevent conflict with already created modals
            if (OTHER_MODALS_CREATED.includes(target)) {
                return;
            }
            if (!triggerId) {
                triggerId = Math.random().toString(36).substr(2, 9);
                element.id = triggerId;
            }
            /**
             * Keep track of modals initialized
             * This weird hack is to allow one modal to be opened by multiple different triggers (ie ajax modals)
             * and prevent the trigger attributes modification caused by the a11y-dialog-component destroy method.
             */
            if (!MODALS_CREATED.includes(target)) {
                MODALS_CREATED.push(target);
                Dialogs.create(target, {
                    triggerId,
                    show: true,
                    onClose: onCloseModal,
                    // @ts-ignore
                    documentClass,
                    documentDisabledClass,
                    documentFixedClass,
                    triggerActiveClass,
                });
            }
            else {
                Dialogs.show(target);
            }
            const dialog = document.getElementById(target);
            onOpenModal(dialog, element);
        });
    }
    if (ajaxModalContainersInitialized) {
        return;
    }
    ajaxModalContainersInitialized = true;
    // Create the ajax modals before for some uses (ie cross sell)
    ['ajax-modal', 'ajax-modal2'].forEach((dialogId) => {
        const dialog = document.getElementById(dialogId);
        if (dialog) {
            // @ts-ignore This is a hack to attach data to the dom element
            dialog.defaultClassName = dialog.className;
        }
        Dialogs.create(dialogId, {
            onOpen: onOpenModal,
            onClose: onCloseModal,
        });
    });
}
export default function addDialog(id, { breakpoint = null, breakpointPlus = false, triggerId = null, labelledbyId = null, describedbyId = null, show = false, modal = true, tooltip = false, backdrop = true, disableScroll = true, disableIosScroll = false, disableEscape = false, transitionDuration = 200, } = {}) {
    const dialog = document.getElementById(id);
    let isCreated = false;
    if (!dialog)
        return;
    const createDialog = () => {
        Dialogs.create(id, {
            triggerId,
            labelledbyId,
            describedbyId,
            show,
            modal,
            tooltip,
            backdrop,
            disableScroll,
            disableIosScroll,
            disableEscape,
            transitionDuration,
        });
        OTHER_MODALS_CREATED.push(id);
    };
    const destroyDialog = () => {
        Dialogs.destroy(id);
    };
    const setDialogAbove = () => {
        if (window.innerWidth > breakpoint && !isCreated) {
            isCreated = true;
            createDialog();
        }
        else if (window.innerWidth <= breakpoint && isCreated) {
            isCreated = false;
            destroyDialog();
        }
    };
    const setDialogBelow = () => {
        if (window.innerWidth <= breakpoint && !isCreated) {
            isCreated = true;
            createDialog();
        }
        else if (window.innerWidth > breakpoint && isCreated) {
            isCreated = false;
            destroyDialog();
        }
    };
    const onLoad = () => {
        breakpointPlus ? setDialogAbove() : setDialogBelow();
    };
    const onResize = () => {
        breakpointPlus ? setDialogAbove() : setDialogBelow();
    };
    if (breakpoint) {
        window.addEventListener('load', onLoad);
        window.addEventListener('resize', onResize);
    }
    else {
        createDialog();
    }
}
